import React, { useState, useContext, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { ContentWrapper } from 'sopslibrary';
import styled from 'styled-components';
import { Page } from '@superops/ui-design-system';

import { AllTicketsContextProvider } from '@nile/client_management/src/Containers/ClientDetails/AllTicketsTable/AllTicketsContext';

import {
  AccountClient,
  MERGE_TICKET,
  SOFT_TICKET_DELETE,
  UPDATE_TICKETS,
  MARKSPAM_TICKETS,
  GET_PERSONALIZATION_BY_NAME,
  GET_ENTITY_PROPERTIES,
  GET_TICKET_LISTVIEW_META,
  SAVE_PERSONALIZATION
} from '@nile/graphql';
import {
  SkeletonLoading,
  TableContainer,
  ContentDeciderContainer,
  CardPage,
  getTranslatedText,
  toast,
  AppContext,
  useIsAuthorisedHook,
  useUserMSPCheckHook,
  ALL_TABLES,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  fetchValues,
  generateOptions
} from '@nile/mangifera/src/Pebbles/TicketToolbar';

import AllTicketsTable from '@nile/client_management/src/Containers/ClientDetails/AllTicketsTable/AllTicketsTableNew';
import AllTicketsToolbar from '@nile/client_management/src/Containers/ClientDetails/AllTicketsTable/AllTicktesToolbarNew';

const AllRequesterTickets = ({
  intl,
  match,
  client,
  requester,
  globalTabContext
}) => {
  const allowEdit = useIsAuthorisedHook('tickets.edit');
  const [viewConditions, setViewConditions] = useState(null);
  const [records, setRecords] = useState([]);
  const [columnPersonalisation, setColumnPersonalisation] = useState(null);

  const appContext = useContext(AppContext);
  const { user } = appContext;
  const isMSP = useUserMSPCheckHook();

  const {
    data: columnPersonalisationData,
    loading: columnPersonalisationLoading,
    error: columnPersonalizationError
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    fetchPolicy: 'cache-and-network',
    client: AccountClient,
    variables: { userId: user?.userId, category: 'TicketView', name: '1' }
  });

  const {
    data: sortPersonalisationData,
    loading: sortPersonalisationLoading,
    error: sortPersonalizationError
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    fetchPolicy: 'network-only',
    client: AccountClient,
    variables: {
      userId: user?.userId,
      category: 'ClientAllTickets',
      name: match?.params?.id || requester?.userId
    }
  });

  const {
    loading: entityPropertiesLoading,
    data: entityProperties,
    error: entityError
  } = useQuery(GET_ENTITY_PROPERTIES);

  const {
    loading: ticketListViewMetaLoading,
    error: ticketMetaError,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const [
    mergeTickets,
    { loading: mergingTicket, error: mergeError }
  ] = useMutation(MERGE_TICKET, {
    onCompleted() {
      toast('success', getTranslatedText(intl, 'ticket.merge.toast'));
      setRecords([]);
    }
  });

  const [
    updateTickets,
    { loading: updatingTicket, error: updateError }
  ] = useMutation(UPDATE_TICKETS, {
    onCompleted() {
      toast('success', getTranslatedText(intl, 'ticket.update.toast'));
      setRecords([]);
    }
  });

  const [
    softDeleteTickets,
    { loading: deletingTicket, error: deleteError }
  ] = useMutation(SOFT_TICKET_DELETE, {
    onCompleted() {
      toast('success', getTranslatedText(intl, 'ticket.delete.toast'));
      setRecords([]);
    }
  });

  const [
    markSpamTickets,
    { loading: spammingTicket, error: spamError }
  ] = useMutation(MARKSPAM_TICKETS, {
    onCompleted() {
      toast('success', getTranslatedText(intl, 'ticket.spam.toast'));
      setRecords([]);
    }
  });

  const [savePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient
  });

  const saveSortPersonalization = (sortOrder) => {
    const personalisedData = {
      user: {
        firstName: user.firstName,
        userId: user.userId,
        email: user.email,
        lastName: user.lastName
      },
      category: 'ClientAllTickets',
      value:
        sortOrder && sortOrder.length ? JSON.stringify({ sort: sortOrder }) : ''
    };
    savePersonalization({
      variables: {
        ...personalisedData,
        name: match?.params?.id || requester?.userId
      }
    });
  };

  const ticketContextValue = {
    records,
    setRecords,
    viewConditions,
    setViewConditions,
    mergeTickets,
    softDeleteTickets,
    markSpamTickets,
    updateTickets
  };

  const loading =
    columnPersonalisationLoading ||
    spammingTicket ||
    deletingTicket ||
    mergingTicket ||
    entityPropertiesLoading ||
    ticketListViewMetaLoading ||
    sortPersonalisationLoading;

  const error = entityError || ticketMetaError;
  const tableName = ALL_TABLES.clientTickets;

  const defaultColumns = [
    {
      name: 'prioritizedId',
      label: 'Prioritized Id',
      attributePlaceHolder: {
        key: 'attribute.prioritizedId',
        label: 'Prioritized Id'
      },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'string',
      entity: null,
      entityType: null,
      uiDataType: 'read_only',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'prioritizedId'
    },
    {
      name: 'subject',
      label: 'Subject',
      attributePlaceHolder: { key: 'attribute.subject', label: 'Subject' },
      isIdentifier: false,
      isKeyAttribute: true,
      dataType: 'string',
      entity: null,
      entityType: null,
      uiDataType: 'text',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'subject'
    },
    {
      name: 'status',
      label: 'Status',
      attributePlaceHolder: { key: 'common.status', label: 'Status' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'string',
      entity: null,
      entityType: null,
      uiDataType: 'select',
      conditionPlaceHolders: null,
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'status'
    },
    {
      name: 'client',
      label: 'Client',
      attributePlaceHolder: { key: 'entity.client', label: 'Client' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'entity',
      entity: 'Client',
      entityType: 'REFER_ENTITY',
      uiDataType: 'select',
      conditionPlaceHolders: [
        { key: 'placeholder.is.loggedin.user', label: 'Is Logged in User' }
      ],
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'client'
    },
    {
      name: 'technician',
      label: 'Technician',
      autoComplete: true,
      attributePlaceHolder: { key: 'entity.technician', label: 'Technician' },
      isIdentifier: false,
      isKeyAttribute: false,
      dataType: 'entity',
      entity: 'Technician',
      entityType: 'REFER_ENTITY',
      uiDataType: 'select',
      conditionPlaceHolders: [
        { key: 'placeholder.is.loggedin.user', label: 'Is Logged in User' }
      ],
      isCustom: false,
      nestedTo: null,
      showToClient: true,
      id: 'technician'
    }
  ];

  return (
    <AllTicketsContextProvider value={ticketContextValue}>
      {/* <TicketTableWrapper className="full-width full-height"> */}
      {/* <CardPage
        className="full-height full-width"
        maxWidth="100%"
        headerPadding="0 0 0 2rem"
        tableContent
      > */}
      <div className="full-height">
        <ContentDeciderContainer
          hasContent
          fullHeight
          loadingContent={
            <div>
              <SkeletonLoading hideStepNav hideSecondaryHeader hideHeader />
            </div>
          }
          isLoading={loading}
          error={error}
          mainContent={
            <>
              <AllTicketsToolbar
                tableName={tableName}
                defaultColumns={defaultColumns}
                onlyResetFilter
              />
              <AllTicketsTable
                ticketViewMeta={ticketListViewMeta?.getTicketListViewMetaInfo}
                globalTabContext={globalTabContext}
                entitiesMeta={entityProperties?.getEntitiesMeta}
                defaultFilter={{
                  joinOperator: 'AND',
                  operands: [
                    {
                      attribute: 'requester.userId',
                      operator: 'is',
                      value: requester.userId
                    }
                  ]
                }}
                defaultPersonalisation={defaultColumns}
                sortPersonalisation={
                  sortPersonalisationData?.getPersonalizationByCategoryAndName
                    ?.value
                    ? JSON.parse(
                        sortPersonalisationData
                          .getPersonalizationByCategoryAndName.value
                      )?.sort
                    : null
                }
                savePersonalisation={saveSortPersonalization}
                noBulkSelect
                padded="lg"
                extraOptions={{ view: { id: isMSP ? '1' : '15' } }}
                tableName={tableName}
                // allowEdit={allowEdit}
                overrideHelpers={{
                  fetchValues: (requester, query, params) => {
                    return fetchValues(requester, query, params, {
                      getRequesterList: [
                        {
                          attribute: 'requester.userId',
                          operator: 'is',
                          value: requester.userId
                        }
                      ]
                    });
                  },
                  generateOptions: (f, data) => {
                    return generateOptions(f, data, {
                      accountId: requester.userId
                    });
                  }
                }}
              />
            </>
          }
        />
      </div>
      {/* </Page> */}
      {/* </CardPage> */}
      {/* </TicketTableWrapper> */}
    </AllTicketsContextProvider>
  );
};

export default injectIntl(withRouter(AllRequesterTickets));
